<script setup lang="ts">
    import { X } from 'lucide-vue-next';
    import { Button } from '@/shared/ui/atoms/Button';
    import { useModalContext } from './context';

    const props = withDefaults(
        defineProps<{
            /** The modal title, which names the dialog. The default slot replaces it */
            title?: string;
            /** A line under the title, which describes the dialog. The `description` slot replaces it */
            description?: string;
            /** Show the × close button. Turn it off when the modal must end through its own buttons */
            closable?: boolean;
        }>(),
        { title: undefined, description: undefined, closable: true },
    );

    const slots = defineSlots<{
        /** The title */
        default?(): unknown;
        /** The line under the title */
        description?(): unknown;
        /** Content between the title and the close button (a badge, an action) */
        actions?(): unknown;
    }>();

    const { titleId, descriptionId, hasTitle, hasDescription, close } = useModalContext('ModalHeader');
    const { t } = useI18n();

    const showTitle = computed(() => !!slots.default || !!props.title);
    const showDescription = computed(() => !!slots.description || !!props.description);
    watch(showTitle, (value) => (hasTitle.value = value), { immediate: true });
    watch(showDescription, (value) => (hasDescription.value = value), { immediate: true });
    onBeforeUnmount(() => {
        hasTitle.value = false;
        hasDescription.value = false;
    });
</script>

<template>
    <div
        data-slot="modal-header"
        class="ui-modal-header"
    >
        <div class="ui-modal-header__text">
            <!-- A div, not a heading: unlayered global heading styles would override the size. The dialog is named by it -->
            <div
                :id="showTitle ? titleId : undefined"
                class="ui-modal-header__title"
            >
                <slot>{{ title }}</slot>
            </div>
            <div
                v-if="showDescription"
                :id="descriptionId"
                class="ui-modal-header__description"
            >
                <slot name="description">{{ description }}</slot>
            </div>
        </div>
        <slot name="actions" />
        <Button
            v-if="closable"
            variant="secondary"
            size="sm"
            icon
            :aria-label="t('CLOSE')"
            @click="close"
        >
            <X />
        </Button>
    </div>
</template>

<style scoped>
    @layer components {
        .ui-modal-header {
            display: flex;
            flex-shrink: 0;
            align-items: flex-start;
            gap: 8px;
            padding: 16px 24px;
        }

        .ui-modal-header__text {
            display: flex;
            flex: 1 1 0%;
            flex-direction: column;
            gap: 4px;
            min-width: 0;
        }

        .ui-modal-header__title {
            font-size: var(--text-xl);
            font-weight: 600;
            line-height: var(--text-xl--line-height);
        }

        .ui-modal-header__description {
            color: var(--color-content-secondary);
            font-size: var(--text-sm);
            line-height: var(--text-sm--line-height);
        }
    }
</style>

<i18n lang="yaml">
en:
    CLOSE: Close
nl:
    CLOSE: Sluiten
</i18n>
